/**
 * Branding context — resolves the academy for the current hostname (or
 * `?academy=<slug>` override during local dev) and injects the academy's
 * primary + accent colours as CSS custom properties so the rest of the app
 * picks them up automatically. Spec §14.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { env } from './env.js'

type LoginMethod = 'email' | 'google'

export interface Branding {
  id: string
  name: string
  slug: string
  logoUrl: string | null
  primaryColor: string | null
  accentColor: string | null
  loginMethods: LoginMethod[]
}

interface BrandingState {
  branding: Branding | null
  loading: boolean
}

const BrandingContext = createContext<BrandingState>({
  branding: null,
  loading: true,
})

// The platform's own hosts — these show the default shell, not tenant branding.
// Tenant academies live on `<slug>.brightstars.in` (and any attached custom
// domain), which fall through to the /academy/resolve lookup below.
const PLATFORM_HOSTS = new Set([
  'brightstars.in',
  'www.brightstars.in',
  'stage.brightstars.in',
  'academiaportal.com',
  'www.academiaportal.com',
  'localhost',
  '127.0.0.1',
])

function resolveLookupKey(): string | null {
  if (typeof window === 'undefined') return null
  const hostname = window.location.hostname

  // Local dev override — `?academy=<slug>` lets us preview tenant branding
  // without needing a real subdomain.
  const params = new URLSearchParams(window.location.search)
  const override = params.get('academy')
  if (override) return override.toLowerCase()

  // Platform host = no branding (the marketing/admin shell stays default).
  if (PLATFORM_HOSTS.has(hostname)) return null
  return hostname
}

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<Branding | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const key = resolveLookupKey()
    if (!key) {
      setLoading(false)
      return
    }

    let cancelled = false
    fetch(`${env.API_URL}/api/v1/academy/resolve?domain=${encodeURIComponent(key)}`)
      .then(async (res) => {
        if (!res.ok) return null
        const json = (await res.json()) as { data?: Branding }
        return json.data ?? null
      })
      .catch(() => null)
      .then((data) => {
        if (cancelled) return
        if (data) {
          setBranding(data)
          if (data.primaryColor) {
            document.documentElement.style.setProperty(
              '--color-primary',
              data.primaryColor,
            )
            // Also derive a hover shade — 12% darker via mix-color fallback.
            document.documentElement.style.setProperty(
              '--color-primary-hover',
              data.primaryColor,
            )
          }
          if (data.accentColor) {
            document.documentElement.style.setProperty(
              '--color-accent',
              data.accentColor,
            )
          }
        }
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo<BrandingState>(() => ({ branding, loading }), [branding, loading])

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBranding(): BrandingState {
  return useContext(BrandingContext)
}
